import {useState} from 'react';

import {FileActionsBar} from '../FileActionsBar/FileActionsBar';
import {FileIcon} from '../FileIcon/FileIcon';
import {Typography} from '../Typography/Typography';

export interface DoneItemProps {
  fileName: string;
  sizeLabel: string;
  uploadedAt?: string;
  onRemove?: () => Promise<void>;
  onDownload?: () => Promise<void>;
  onView?: () => Promise<void>;
  isLoading: boolean;
  ext: string;
  deleteLabel?: string;
  downloadLabel?: string;
  viewLabel?: string;
}

export function DoneItem({
  fileName,
  sizeLabel,
  uploadedAt,
  onRemove,
  onDownload,
  onView,
  isLoading,
  ext,
  deleteLabel = 'Delete',
  downloadLabel = 'Download',
  viewLabel = 'View'
}: DoneItemProps) {
  const [activeAction, setActiveAction] = useState<'remove' | 'download' | 'view' | null>(null);

  const handleAction = async (
    action: 'remove' | 'download' | 'view',
    handler: () => Promise<void>
  ) => {
    setActiveAction(action);
    try {
      await handler();
    } finally {
      setActiveAction(null);
    }
  };

  const isBusy = isLoading || activeAction !== null;

  return (
    <div
      data-slot="file-upload-item"
      data-state="done"
      className="flex w-full items-center justify-between gap-3 rounded-md bg-background-tertiary p-3"
    >
      {/* File info */}
      <div className="flex min-w-0 items-center gap-3">
        <FileIcon ext={ext} size="sm" />
        <div className="flex min-w-0 flex-col gap-0.5">
          <Typography size="sm">{fileName}</Typography>
          <div className="flex items-center gap-1 text-xs text-fg-placeholder">
            <span dir="ltr">{sizeLabel}</span>
            {uploadedAt && (
              <>
                <span aria-hidden="true">|</span>
                <span>{uploadedAt}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <FileActionsBar
        onPreview={onView ? () => void handleAction('view', onView) : undefined}
        onDownload={onDownload ? () => void handleAction('download', onDownload) : undefined}
        onDelete={onRemove ? () => void handleAction('remove', onRemove) : undefined}
        previewLabel={viewLabel}
        downloadLabel={downloadLabel}
        deleteLabel={deleteLabel}
        isPreviewing={activeAction === 'view'}
        isDownloading={activeAction === 'download'}
        isDeleting={activeAction === 'remove'}
        disabled={isBusy}
      />
    </div>
  );
}
